import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import { useSales } from '@/hooks/useSales';
import { downloadCsv } from '@/lib/csv';
import { METHOD_LABELS } from '@/services/payments';
import SaleModal from './components/SaleModal';
import SaleDetailModal from './components/SaleDetailModal';

export default function SalesPage() {
  const { data: sales, loading, error, refetch } = useSales();
  const [showNew, setShowNew] = useState(false);
  const [detailSaleId, setDetailSaleId] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const filteredSales = sales.filter((s) => {
    if (!s.saleDate) return true;
    const d = new Date(s.saleDate);
    if (dateFrom && d < new Date(`${dateFrom}T00:00:00`)) return false;
    if (dateTo && d > new Date(`${dateTo}T23:59:59`)) return false;
    return true;
  });

  const totalSales = filteredSales.reduce((sum, s) => sum + s.total, 0);
  const totalProfit = filteredSales.reduce((sum, s) => sum + s.grossProfit, 0);
  const avgTicket = filteredSales.length > 0 ? totalSales / filteredSales.length : 0;

  function exportSales() {
    downloadCsv(
      'ventas',
      ['Venta', 'Cliente', 'Vendedor', 'Canal', 'Medio de pago', 'Total', 'Costo', 'Utilidad', 'Fecha'],
      filteredSales.map((s) => [
        s.saleNumber,
        s.customerName,
        s.salespersonName,
        s.channel,
        s.paymentMethod ? METHOD_LABELS[s.paymentMethod] || s.paymentMethod : '',
        s.total,
        s.costTotal,
        s.grossProfit,
        s.saleDate ? new Date(s.saleDate).toLocaleDateString('es-CO') : '',
      ]),
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Ventas</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{filteredSales.length} ventas · ${totalSales.toLocaleString()} total</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportSales}
              className="text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-download-line mr-1.5"></i>Exportar
            </button>
            <button
              onClick={() => setShowNew(true)}
              className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-add-line mr-1.5"></i>Nueva Venta
            </button>
          </div>
        </div>

        {/* Filtro por fecha */}
        <div className="bg-white rounded-xl border border-background-200 p-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-foreground-700">
            <i className="ri-calendar-line text-foreground-500"></i>
            <span className="font-medium whitespace-nowrap">Filtrar por fecha</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-foreground-500 whitespace-nowrap">Desde</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="text-sm border border-background-200 rounded-lg px-2.5 py-1.5 bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-foreground-500 whitespace-nowrap">Hasta</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="text-sm border border-background-200 rounded-lg px-2.5 py-1.5 bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                }}
                className="text-xs px-3 py-1.5 text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-close-line mr-1"></i>Limpiar
              </button>
            )}
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Ventas Totales</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">${totalSales.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Utilidad Bruta</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">${totalProfit.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Margen Promedio</p>
            <p className="text-xl font-bold text-accent-600 mt-1">
              {totalSales > 0 ? Math.round((totalProfit / totalSales) * 100) : 0}%
            </p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Ticket Promedio</p>
            <p className="text-xl font-bold text-amber-600 mt-1">${Math.round(avgTicket).toLocaleString()}</p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Venta</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Cliente</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden sm:table-cell">Canal</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden sm:table-cell">Medio de pago</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Total</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right hidden md:table-cell">Costo</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Utilidad</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden lg:table-cell">Fecha</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filteredSales.map((sale) => (
                  <tr
                    key={sale.id}
                    className={`border-b transition-colors ${
                      sale.hasOutOfStock
                        ? 'bg-amber-50/60 hover:bg-amber-100/60 border-amber-100'
                        : 'border-background-100 hover:bg-background-50'
                    }`}
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className="text-accent-600 font-medium whitespace-nowrap">{sale.saleNumber}</span>
                        {sale.voided && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 whitespace-nowrap">
                            Anulada
                          </span>
                        )}
                        {sale.hasShipping && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap">
                            Con envío
                          </span>
                        )}
                        {sale.hasOutOfStock && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
                            Sin stock
                          </span>
                        )}
                        {sale.paymentStatus === 'approved' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">
                            Pagado
                          </span>
                        )}
                        {sale.paymentStatus === 'pending' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
                            {sale.paymentMethod === 'cash' ? 'Contra entrega' : 'Pendiente'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <div>
                        <p className="text-foreground-900 font-medium text-sm">{sale.customerName || '—'}</p>
                        {sale.salespersonName && (
                          <p className="text-xs text-foreground-500">Vendedor: {sale.salespersonName}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 hidden sm:table-cell">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 capitalize">
                        {sale.channel || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 hidden sm:table-cell">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                        {sale.paymentMethod
                          ? METHOD_LABELS[sale.paymentMethod] || sale.paymentMethod
                          : '—'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right text-foreground-900 font-medium whitespace-nowrap">
                      ${sale.total.toLocaleString()}
                    </td>
                    <td className="px-5 py-3.5 text-right text-foreground-600 whitespace-nowrap hidden md:table-cell">
                      ${sale.costTotal.toLocaleString()}
                    </td>
                    <td className="px-5 py-3.5 text-right text-emerald-600 font-medium whitespace-nowrap">
                      ${sale.grossProfit.toLocaleString()}
                    </td>
                    <td className="px-5 py-3.5 hidden lg:table-cell text-xs text-foreground-500 whitespace-nowrap">
                      {sale.saleDate
                        ? new Date(sale.saleDate).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })
                        : '—'}
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        onClick={() => setDetailSaleId(sale.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-accent-600 hover:bg-accent-100 transition-colors cursor-pointer"
                        title="Ver detalle"
                        aria-label="Ver detalle"
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && !error && filteredSales.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-inbox-line text-3xl block mb-2"></i>
              <p className="text-sm">No se encontraron ventas</p>
            </div>
          )}
          {loading && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-2xl block mb-2"></i>
              <p className="text-sm">Cargando ventas...</p>
            </div>
          )}
        </div>
      </div>

      {showNew && <SaleModal onClose={() => setShowNew(false)} onSaved={refetch} />}
      {detailSaleId && (
        <SaleDetailModal
          saleId={detailSaleId}
          onClose={() => setDetailSaleId(null)}
          onChanged={refetch}
        />
      )}
    </AdminLayout>
  );
}